import { useEffect, useMemo, useState } from "react";
import {
  api,
  ApiError,
  type ExpertSummary,
  type TranscriptSegment,
} from "../api";
import { ExpertMark } from "../components/design/ExpertMark";
import { Segmented } from "../components/design/Segmented";
import { Icon } from "../components/design/Icon";
import { navigate, routes } from "../router";

type Filter = "all" | "humans" | "experts";

const FILTER_OPTIONS: ReadonlyArray<{ value: Filter; label: string }> = [
  { value: "all", label: "All" },
  { value: "humans", label: "Humans" },
  { value: "experts", label: "Experts" },
];

export function Transcript({ meetingId }: { meetingId: string }) {
  const [segments, setSegments] = useState<TranscriptSegment[] | null>(null);
  const [experts, setExperts] = useState<ExpertSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");

  useEffect(() => {
    let alive = true;
    api
      .getTranscript(meetingId)
      .then((r) => {
        if (alive) setSegments(r.segments);
      })
      .catch((e) => {
        if (!alive) return;
        setError(
          e instanceof ApiError
            ? `${e.message} (HTTP ${e.status})`
            : e instanceof Error
              ? e.message
              : String(e),
        );
      });
    api.listExperts().then(setExperts).catch(() => {});
    return () => {
      alive = false;
    };
  }, [meetingId]);

  const expertById = useMemo(
    () => new Map(experts.map((e) => [e.id, e])),
    [experts],
  );

  const visible = useMemo(() => {
    if (!segments) return [];
    return segments.filter((s) => {
      if (filter === "humans") return !s.expert_id;
      if (filter === "experts") return !!s.expert_id;
      return true;
    });
  }, [segments, filter]);

  const speakerCounts = useMemo(() => {
    const humans = new Map<string, number>();
    const expertCounts = new Map<string, number>();
    for (const s of segments ?? []) {
      if (s.expert_id) {
        expertCounts.set(s.expert_id, (expertCounts.get(s.expert_id) ?? 0) + 1);
      } else {
        humans.set(s.speaker, (humans.get(s.speaker) ?? 0) + 1);
      }
    }
    return { humans, expertCounts };
  }, [segments]);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateRows: "auto 1fr",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      <header
        style={{
          padding: "14px 24px",
          borderBottom: "1px solid var(--rule)",
          background: "var(--card)",
          display: "flex",
          alignItems: "center",
          gap: 16,
        }}
      >
        <button
          type="button"
          className="btn sm"
          onClick={() => navigate(routes.meeting(meetingId))}
        >
          <Icon name="arrowL" size={12} /> Back
        </button>
        <span className="mono dim" style={{ fontSize: 11 }}>
          /meetings/{meetingId.slice(0, 12)}/transcript
        </span>
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            gap: 8,
            alignItems: "center",
          }}
        >
          <button
            type="button"
            className="btn sm"
            onClick={() => downloadJson(meetingId, segments ?? [])}
            disabled={!segments}
          >
            <Icon name="download" size={13} /> .json
          </button>
          <button
            type="button"
            className="btn primary sm"
            onClick={() => downloadTxt(meetingId, segments ?? [], expertById)}
            disabled={!segments}
          >
            <Icon name="download" size={13} /> .txt
          </button>
        </div>
      </header>

      <div
        className="recspec-transcript-body"
        style={{
          display: "grid",
          gridTemplateColumns: "260px 1fr",
          overflow: "hidden",
        }}
      >
        <aside
          style={{
            borderRight: "1px solid var(--rule)",
            padding: 20,
            background: "var(--paper-2)",
            overflow: "auto",
          }}
        >
          <div className="eyebrow" style={{ marginBottom: 10 }}>
            Speakers
          </div>
          {Array.from(speakerCounts.humans.entries()).map(([name, n]) => (
            <SpeakerRow key={name} initial={name[0] ?? "?"} name={name} count={n} />
          ))}
          {speakerCounts.humans.size === 0 && segments && segments.length > 0 && (
            <div className="dim" style={{ fontSize: 12 }}>
              (no human speakers)
            </div>
          )}
          <div className="hr" style={{ margin: "12px 0" }} />
          {Array.from(speakerCounts.expertCounts.entries()).map(([id, n]) => {
            const e = expertById.get(id);
            if (!e) return null;
            return (
              <div
                key={id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "6px 0",
                }}
              >
                <ExpertMark expert={e} size={22} />
                <span style={{ fontSize: 13 }}>{e.short_name}</span>
                <span
                  className="mono dim"
                  style={{ fontSize: 10.5, marginLeft: "auto" }}
                >
                  {n}
                </span>
              </div>
            );
          })}
          <div className="hr" style={{ margin: "16px 0 10px" }} />
          <div className="eyebrow" style={{ marginBottom: 8 }}>
            Filter
          </div>
          <Segmented<Filter>
            value={filter}
            onChange={setFilter}
            size="sm"
            options={FILTER_OPTIONS}
          />
          <div
            className="dim"
            style={{ fontSize: 11.5, marginTop: 14, lineHeight: 1.5 }}
          >
            Transcript is available <strong>during</strong> and{" "}
            <strong>after</strong> the meeting — independent of the bundle.
          </div>
        </aside>

        <div
          style={{
            overflow: "auto",
            padding: "24px 32px",
            background: "var(--paper)",
          }}
        >
          <div style={{ maxWidth: 760, margin: "0 auto" }}>
            <div className="eyebrow" style={{ marginBottom: 6 }}>
              /meetings/{meetingId.slice(0, 12)}/transcript
            </div>
            <h1
              className="serif"
              style={{
                margin: 0,
                fontSize: 36,
                fontWeight: 400,
                letterSpacing: "-.01em",
              }}
            >
              The transcript so far.
            </h1>
            <div className="dim" style={{ marginTop: 6, fontSize: 13 }}>
              {visible.length} segments · auto-punctuated · powered by Recall.ai
              managed
            </div>

            {error && (
              <div
                style={{
                  marginTop: 16,
                  color: "var(--err)",
                  font: "var(--type-body-sm)",
                }}
              >
                {error}
              </div>
            )}

            {!segments && !error && (
              <div className="dim" style={{ marginTop: 16 }}>
                Loading transcript…
              </div>
            )}

            <div
              style={{
                marginTop: 26,
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}
            >
              {visible.map((s, i) => {
                const e = s.expert_id ? expertById.get(s.expert_id) : undefined;
                return (
                  <div
                    key={i}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "70px 1fr",
                      gap: 16,
                      alignItems: "start",
                    }}
                  >
                    <div
                      className="mono"
                      style={{
                        fontSize: 11,
                        color: "var(--ink-3)",
                        paddingTop: 4,
                      }}
                    >
                      {fmtTime(s.start_seconds)}
                    </div>
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 4,
                        }}
                      >
                        {e ? (
                          <>
                            <ExpertMark expert={e} size={20} />
                            <strong
                              style={{
                                fontSize: 12.5,
                                color: `var(--${e.color_token}-deep)`,
                              }}
                            >
                              {e.short_name}
                            </strong>
                            <span
                              className="mono dim"
                              style={{ fontSize: 10.5 }}
                            >
                              · spoken via bot
                            </span>
                          </>
                        ) : (
                          <>
                            <span
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: "50%",
                                background: "var(--paper-3)",
                                border: "1px solid var(--rule-2)",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 10,
                                fontWeight: 600,
                              }}
                            >
                              {(s.speaker[0] ?? "?").toUpperCase()}
                            </span>
                            <strong style={{ fontSize: 12.5 }}>
                              {s.speaker}
                            </strong>
                          </>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 14.5,
                          lineHeight: 1.6,
                          color: e ? `var(--${e.color_token}-deep)` : "var(--ink)",
                        }}
                      >
                        {s.text}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SpeakerRow({
  initial,
  name,
  count,
}: {
  initial: string;
  name: string;
  count: number;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "6px 0",
      }}
    >
      <span
        style={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: "var(--paper-3)",
          border: "1px solid var(--rule-2)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 10,
          fontWeight: 600,
          color: "var(--ink-2)",
        }}
      >
        {initial.toUpperCase()}
      </span>
      <span style={{ fontSize: 13 }}>{name}</span>
      <span className="mono dim" style={{ fontSize: 10.5, marginLeft: "auto" }}>
        {count}
      </span>
    </div>
  );
}

function fmtTime(sec: number): string {
  const total = Math.max(0, Math.floor(sec));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function downloadJson(meetingId: string, segments: TranscriptSegment[]) {
  const blob = new Blob([JSON.stringify({ segments }, null, 2)], {
    type: "application/json",
  });
  triggerDownload(blob, `${meetingId}-transcript.json`);
}

function downloadTxt(
  meetingId: string,
  segments: TranscriptSegment[],
  expertById: Map<string, ExpertSummary>,
) {
  const lines = segments.map((s) => {
    const t = fmtTime(s.start_seconds);
    const who = s.expert_id
      ? `[${expertById.get(s.expert_id)?.short_name ?? s.expert_id}]`
      : s.speaker;
    return `${t}  ${who}: ${s.text}`;
  });
  const blob = new Blob([lines.join("\n")], { type: "text/plain" });
  triggerDownload(blob, `${meetingId}-transcript.txt`);
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
